import {
  applyFilteringToUrl,
  applyPagingToUrl,
  applySortingToUrl,
  buildApiUrl,
  sendDeleteRequest,
  sendGetRequest,
  sendPaginationRequest,
  sendPatchRequest,
  sendPostRequest,
  sendPutRequest,
  Type
} from '@/api/api'
import { format } from 'date-fns/format'
import { DateFormats } from '@/helpers/formatting'
import type { SortedField } from '@/types'
import {
  BillingChargeSchema,
  CreateBillingChargeSchema, DashboardDocumentType,
  InvoiceConfigSchema,
  InvoiceMeterReadingsSchema,
  InvoiceSchema,
  PaginatedInvoiceSchema,
  UpdateBillingChargeValuesSchema,
  type UpdateInvoiceMeterReading
} from '@/api/billing/schemas'
import { InvoiceStatus } from '@/api/billing/constants'
import { assertSchema, assertSchemaAndReturn } from '@/helpers/assert-schema'
import { z } from 'zod'
import { objectWithoutKeys, withoutKeys } from '@/helpers'


export async function regenerateInvoice(invoiceId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/regenerate`)

  // Endpoint always returns `true` and will throw an error if there's an issue.
  await sendPostRequest(url)
}


export async function recreateInvoice(invoiceId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/recreate`)
  await sendPostRequest(url)
}


export async function sendInvoice(invoiceId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/send`)

  await sendPostRequest(url)
}


export async function cancelInvoice(invoiceId: number) {
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/cancelled`)

  await sendPutRequest(url)
}


export async function getPaginatedInvoices(params: Partial<{
  page: number
  pageSize: number
  sort: SortedField[]
  statuses: InvoiceStatus[]
  customerIds: number[]
  startAt: Date
  endAt: Date
  filter: string
}>) {
  const url = buildApiUrl(Type.AssetManagement, 'billing/invoices')

  applyPagingToUrl(url, params.page, params.pageSize)
  applyFilteringToUrl(url, params.filter)
  applySortingToUrl(url, params.sort)

  if (params.customerIds !== undefined && params.customerIds.length > 0) {
    url.searchParams.set('customers', params.customerIds.join(','))
  }

  if (params.startAt !== undefined) {
    url.searchParams.set('start', format(params.startAt, DateFormats.YearMonthDay))
  }

  if (params.endAt !== undefined) {
    url.searchParams.set('end', format(params.endAt, DateFormats.YearMonthDay))
  }

  if (params.filter !== undefined && params.filter.trim().length > 0) {
    url.searchParams.set('filter', params.filter.trim())
  }

  if (params.statuses !== undefined && params.statuses.length > 0) {
    url.searchParams.set('status', params.statuses.map(x => x.valueOf()).join(','))
  }

  const { paging, records } = await sendPaginationRequest(url, PaginatedInvoiceSchema)

  return {
    paging,
    records: records.map(
      x => ({
        ...x,
        previewUrl:x.documentType=== DashboardDocumentType.invoice ? buildApiUrl(Type.AssetManagement, `billing/invoices/${x.id}/preview`).toString() : buildApiUrl(Type.AssetManagement, `billing/credit-notes/${x.id}/preview`).toString(),
        downloadUrl: x.documentType=== DashboardDocumentType.invoice ? buildApiUrl(Type.AssetManagement, `billing/invoices/${x.id}/files`).toString() : buildApiUrl(Type.AssetManagement, `billing/credit-notes/${x.id}/files`).toString(),
        solarReportUrl: buildApiUrl(Type.AssetManagement, `billing/invoices/${x.id}/reports/solar`).toString(),
        solarReportHourlyValues: buildApiUrl(Type.AssetManagement, `billing/invoices/${x.id}/reports/solar/hourly-values`).toString()
      })
    )
  }
}

export async function getInvoiceConfig(assetCode: string){

  const url = buildApiUrl(Type.AssetManagement, `assets/${assetCode}/billings/invoices/dummies`)

  const { data } = await sendGetRequest(url)

  assertSchema(data, InvoiceConfigSchema)

  return data
}

export const getBillingCharge = (id: number) => sendGetRequest(buildApiUrl(Type.AssetManagement, `billing/invoices/charges/${id}`))
.then(({data}) => {
  return assertSchemaAndReturn(data, BillingChargeSchema)
})

type billingConfig = {
  id: number,
  showMeterReadings: boolean,
  showSavings: boolean
}

export async function saveInvoiceConfig(assetCode: string, billingConfig : billingConfig){

  const url = buildApiUrl(Type.AssetManagement, `assets/${assetCode}/billing/invoices/billing-configurations/${billingConfig.id}`)

  await sendPatchRequest(url, { data: billingConfig })
}

export async function getInvoice(invoiceId: number){

  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}?includeCustomer=true`)

  const { data } = await sendGetRequest(url)

  assertSchema(data, InvoiceSchema)

  return data
}

export async function saveCharges(invoiceId: number, invoiceCharges : { id: number, quantity: number, description: string }[]){
    const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/charges`)

    await sendPatchRequest(url, { data: invoiceCharges })
}

export async function saveNotes(invoiceId: number, notes: string){
    const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/notes`)

    await sendPatchRequest(url, { data: notes })
}
type InvoiceConfig = {
  invoiceId: number,
  showMeterReadings: boolean,
  showSavings: boolean,
  poNumber: string
}
export async function updateInvoiceConfig(invoiceConfig : InvoiceConfig){
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceConfig.invoiceId}/configurations`)
  await sendPatchRequest(url,{ data: invoiceConfig })
}

export async function updateInvoiceMeterReadings(invoiceId: number, invoiceMeterReading: UpdateInvoiceMeterReading[], updatingConfig: boolean){
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/meters?updatingConfig=${updatingConfig}`)
  await sendPutRequest(url, { data: invoiceMeterReading })
}

export async function getInvoiceMeters(invoiceId : number){
  const url = buildApiUrl(Type.AssetManagement, `billing/invoices/${invoiceId}/meters`)
  const { data } = await sendGetRequest(url)

  assertSchema(data, z.array(InvoiceMeterReadingsSchema))
  return data
}

export async function updateRecurringCharge(assetCode: string, charge: z.infer<typeof UpdateBillingChargeValuesSchema>) {

  assertSchema(charge, UpdateBillingChargeValuesSchema)

  const url = buildApiUrl(Type.AssetManagement, `assets/${assetCode}/invoices/charges/${charge.id}`)

  const data = withoutKeys(charge, ['id'])

  await sendPutRequest(url, { data })
}

export async function removeRecurringCharge(assetCode: string, chargeId: number) {
  await sendDeleteRequest(
    buildApiUrl(Type.AssetManagement, `assets/${assetCode}/invoices/charges/${chargeId}`)
  )
}

export async function createRecurringCharge(assetCode: string, charge: z.infer<typeof CreateBillingChargeSchema>) {

  assertSchema(charge, CreateBillingChargeSchema)

  const url = buildApiUrl(Type.AssetManagement, `assets/${assetCode}/invoices/charges`)
  const data = objectWithoutKeys(charge, ['rateInFractionalCurrency']) as Record<string, any>
  data.rateInCents = charge.rateInFractionalCurrency

  await sendPostRequest(url, { data })
}


